<script setup lang="ts">
import { Link, usePage } from '@inertiajs/vue3';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AppLogoIcon from '@/components/AppLogoIcon.vue';
import { AUTH_ROUTES, LEGAL_ROUTES, MARKETING_ROUTES } from '@/config/constants/routes';
import { SOCIAL_NETWORKS } from '@/config/constants/social';
import type { SharedData } from '@/types';

const { t } = useI18n();

const page = usePage<SharedData>();

const appName = computed(() => page.props.name);

/**
 * Only networks with a configured URL are rendered — an empty <a href="#">
 * is a dead link for a reader and a wasted crawl for a search engine.
 */
const socialLinks = computed(() =>
    SOCIAL_NETWORKS.map((network) => ({ ...network, url: page.props.socialProfiles?.[network.key] })).filter(
        (network): network is typeof network & { url: string } => Boolean(network.url),
    ),
);
</script>

<template>
    <footer class="relative border-t border-teal-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        <div class="container mx-auto px-6 py-16 lg:px-8">
            <div class="grid gap-12 lg:grid-cols-12 lg:gap-8">
                <!-- Brand Column -->
                <div class="lg:col-span-5">
                    <Link href="/" class="group inline-flex items-center">
                        <AppLogoIcon class="h-8" />
                    </Link>
                    <p class="mt-6 max-w-sm leading-relaxed text-teal-500">{{ t('footer.tagline') }}</p>

                    <!-- Social Links -->
                    <div v-if="socialLinks.length > 0" class="mt-8 flex items-center gap-4">
                        <a
                            v-for="network in socialLinks"
                            :key="network.key"
                            :href="network.url"
                            target="_blank"
                            rel="noopener me"
                            :aria-label="t('footer.follow_on', { network: network.label })"
                            class="flex h-10 w-10 items-center justify-center rounded-full border border-teal-200 text-teal-500 transition-all hover:border-teal-900 hover:text-teal-900 dark:border-gray-700 dark:hover:border-white dark:hover:text-white"
                        >
                            <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path :d="network.path" />
                            </svg>
                        </a>
                    </div>
                </div>

                <!-- Links Columns -->
                <div class="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:col-span-7">
                    <!-- Platform -->
                    <div>
                        <h4 class="text-sm font-medium tracking-widest text-teal-400" style="letter-spacing: 0.1em">
                            {{ t('footer.platform') }}
                        </h4>
                        <ul class="mt-6 space-y-4">
                            <li>
                                <a
                                    href="/#features"
                                    class="text-sm text-teal-600 transition-colors hover:text-teal-900 dark:text-gray-400 dark:hover:text-white"
                                >
                                    {{ t('footer.features') }}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/#touri"
                                    class="text-sm text-teal-600 transition-colors hover:text-teal-900 dark:text-gray-400 dark:hover:text-white"
                                >
                                    {{ t('footer.touri_ai') }}
                                </a>
                            </li>
                            <li>
                                <Link
                                    :href="route(MARKETING_ROUTES.pricing)"
                                    class="text-sm text-teal-600 transition-colors hover:text-teal-900 dark:text-gray-400 dark:hover:text-white"
                                >
                                    {{ t('footer.pricing') }}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    :href="route(MARKETING_ROUTES.glossary)"
                                    class="text-sm text-teal-600 transition-colors hover:text-teal-900 dark:text-gray-400 dark:hover:text-white"
                                >
                                    {{ t('footer.glossary') }}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    :href="route(MARKETING_ROUTES.help)"
                                    class="text-sm text-teal-600 transition-colors hover:text-teal-900 dark:text-gray-400 dark:hover:text-white"
                                >
                                    {{ t('footer.help') }}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <!-- Get Started -->
                    <div>
                        <h4 class="text-sm font-medium tracking-widest text-teal-400" style="letter-spacing: 0.1em">
                            {{ t('footer.get_started') }}
                        </h4>
                        <ul class="mt-6 space-y-4">
                            <li>
                                <Link
                                    :href="route(AUTH_ROUTES.register)"
                                    class="text-sm text-teal-600 transition-colors hover:text-teal-900 dark:text-gray-400 dark:hover:text-white"
                                >
                                    {{ t('footer.create_account') }}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    :href="route(AUTH_ROUTES.login)"
                                    class="text-sm text-teal-600 transition-colors hover:text-teal-900 dark:text-gray-400 dark:hover:text-white"
                                >
                                    {{ t('footer.sign_in') }}
                                </Link>
                            </li>
                            <li>
                                <a
                                    href="/#contact"
                                    class="text-sm text-teal-600 transition-colors hover:text-teal-900 dark:text-gray-400 dark:hover:text-white"
                                >
                                    {{ t('footer.contact') }}
                                </a>
                            </li>
                        </ul>
                    </div>

                    <!-- Legal -->
                    <div>
                        <h4 class="text-sm font-medium tracking-widest text-teal-400" style="letter-spacing: 0.1em">
                            {{ t('footer.legal') }}
                        </h4>
                        <ul class="mt-6 space-y-4">
                            <li>
                                <Link
                                    :href="route(LEGAL_ROUTES.terms)"
                                    class="text-sm text-teal-600 transition-colors hover:text-teal-900 dark:text-gray-400 dark:hover:text-white"
                                >
                                    {{ t('legal.terms.title') }}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    :href="route(LEGAL_ROUTES.privacy)"
                                    class="text-sm text-teal-600 transition-colors hover:text-teal-900 dark:text-gray-400 dark:hover:text-white"
                                >
                                    {{ t('legal.privacy.title') }}
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- Bottom Bar -->
            <div class="mt-16 flex flex-col items-center justify-between gap-6 border-t border-teal-200 pt-8 md:flex-row dark:border-gray-700">
                <p class="text-sm text-teal-500">&copy; {{ new Date().getFullYear() }} {{ appName }}. All rights reserved.</p>
                <div class="flex flex-wrap items-center justify-center gap-6 text-sm text-teal-500">
                    <Link :href="route(LEGAL_ROUTES.terms)" class="transition-colors hover:text-teal-900 dark:hover:text-white">
                        {{ t('legal.terms.title') }}
                    </Link>
                    <Link :href="route(LEGAL_ROUTES.privacy)" class="transition-colors hover:text-teal-900 dark:hover:text-white">
                        {{ t('legal.privacy.title') }}
                    </Link>
                </div>
            </div>
        </div>
    </footer>
</template>
